import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import DegradedQualityIndicator from '../shared/DegradedQualityIndicator';
import { format } from '../../../../../units';
import LabelWithTooltip from '../../../../LabelWithTooltip';
import { getVariableById } from '../../../../../bundles/sources';
import { getTimezone } from '../../../../../bundles/application';

const MetricVisualization = ({
  visualization,
  isPreview = false,
  disconnected,
  latestValues: values,
  initialLoaded: initialLoadedOverride,
}) => {
  const { configuration = {} } = visualization;
  const timezone = useSelector(getTimezone);
  const initialLoaded = initialLoadedOverride ?? Boolean(values);
  const [firstVariable] = values || [];
  const { variable: variableId } = firstVariable || {};
  const sourceVariable = useSelector((state) => getVariableById(state, variableId));

  const [value, quality] = useMemo(() => {
    if (initialLoaded && values) {
      if (!firstVariable?.value || firstVariable.value.length === 0) {
        return [null, 2];
      }
      const [, lastValue, lastValueQuality = 1] = firstVariable.value;
      if (lastValue || lastValue === 0) {
        const currentValue = Number.isNaN(lastValue) ? null : lastValue;
        return [currentValue, currentValue === null ? 2 : lastValueQuality];
      }
      return [null, 2];
    }
    return [null, 0];
  }, [initialLoaded, values, firstVariable]);

  // Determine the unit label to use for formatting if the user selects a unit from the dropdown other than the default.
  // If no custom unit label is added, configuration.unitLabel is undefined.
  const unitLabel =
    configuration.unit !== 'variable-default' && !configuration.unitLabel
      ? configuration.unit
      : configuration.unitLabel;

  const displayUnit =
    configuration.unit === 'variable-default' ? sourceVariable?.unit || '' : configuration.unit;
  const { value: formattedValue, unit } = format(
    displayUnit,
    value ?? configuration.defaultEmptyValue,
    configuration.decimals,
    unitLabel,
    timezone
  );

  // invalidate if quality !== 1 (valid) or no data connectivity (disconnected)
  const isDataInvalid = quality > 1 || disconnected;

  return (
    <div
      className={`metric-visualization-component ${isPreview ? 'preview' : 'normal'} ${isDataInvalid ? 'invalid-data' : ''}`}
    >
      <div className="wrapper">
        <div className="label">
          <LabelWithTooltip name={configuration?.label || visualization?.name || '\u00A0'} />
          {isDataInvalid ? <DegradedQualityIndicator /> : null}
        </div>
        <div className="value-wrapper">
          <div className="value">{formattedValue}</div>
          {(value || value === 0) && <div className="unit">{configuration.unitLabel || unit} </div>}
        </div>
      </div>
    </div>
  );
};

export default MetricVisualization;
